import { ApiQueryKeys } from 'api/common/queryKeys'
import { useCreateDeploymentPlan } from 'api/deploymentPlans-v2/mutations/useCreateDeploymentPlan'
import { useUpdateDeploymentPlan } from 'api/deploymentPlans-v2/mutations/useUpdateDeploymentPlan'
import { useDeploymentPlan } from 'api/deploymentPlans-v2/queries/useDeploymentPlan'
import { useExperiences } from 'api/experiences-v2/queries/useExperiences'
import { usePolicyDocuments } from 'api/policyDocuments/queries/usePolicyDocuments'
import { usePolicyScopes } from 'api/policyScopes/queries/usePolicyScopes'
import { useThemes } from 'api/themes-v3/queries/useThemes'
import { showToast } from 'components/modals/AlertComponent'
import { MaybeNull } from 'interfaces'
import { DeploymentPlanDTO } from 'interfaces/deploymentPlans-v2/deploymentPlan'
import { ENTITLEMENTS } from 'interfaces/entitlements/entitlements'
import { ExperienceV2DTO } from 'interfaces/experiences-v2/experience'
import { PolicyDocumentDTO } from 'interfaces/policyDocuments/policyDocument'
import { PolicyScopeDTO } from 'interfaces/policyScopes-v2/policyScope'
import { ThemeV3DTO } from 'interfaces/themes-v3/theme'
import { DeploymentPlanFormValues, mapValuesToPayload } from 'pages/consentAndRights/deploymentPlans-v2/upsert/utils'
import React, { useCallback, useMemo } from 'react'
import { useQueryClient } from 'react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { NEW_ITEM_CODE } from 'utils/constants/misc'
import { useIsEntitled, useQueryParams } from 'utils/hooks'
import { createCustomContext } from 'utils/hooks/createCustomContext'
import { RoutesManager } from 'utils/routing/routesManager'

export interface DeploymentPlanUpsertContextValues {
  isReady: boolean
  onSubmit: (values: DeploymentPlanFormValues) => Promise<void>
  duplicate: boolean
  deploymentPlan: MaybeNull<DeploymentPlanDTO>
  policyScopes: PolicyScopeDTO[]
  experiences: ExperienceV2DTO[]
  policyDocuments: PolicyDocumentDTO[]
  themes: ThemeV3DTO[]
}

const { Provider, useCustomContext: useDeploymentPlanUpsertContext } =
  createCustomContext<DeploymentPlanUpsertContextValues>({
    displayName: 'DeploymentPlanUpsertContext',
  })

export const DeploymentPlanUpsertContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { code } = useParams<{ code: string }>()
  const { queries } = useQueryParams<{ duplicate?: string }>()
  const { isEntitled } = useIsEntitled()

  const duplicate = Boolean(queries.duplicate)
  const isEditMode = code !== NEW_ITEM_CODE
  const isEntitledToContentAndDisclosure = isEntitled(ENTITLEMENTS.EXP_SERVER_CONSENT_AND_DISCLOSURE)

  const { data: deploymentPlan, isLoading: isDeploymentPlanLoading } = useDeploymentPlan({
    enabled: duplicate || isEditMode,
    params: {
      deploymentPlanCode: code!,
    },
    onError: () => {
      navigate(RoutesManager.deployment.deploymentPlansV2.root.getURL())
    },
  })

  const { data: policyScopes, isLoading: isPolicyScopesLoading } = usePolicyScopes()
  const { data: experiences, isLoading: isExperiencesLoading } = useExperiences()
  const { data: policyDocuments, isLoading: isPolicyDocumentLoading } = usePolicyDocuments()
  const { data: themes, isLoading: isThemesLoading } = useThemes()
  const { mutateAsync: handleCreateDeploymentPlan } = useCreateDeploymentPlan()
  const { mutateAsync: handleUpdateDeploymentPlan } = useUpdateDeploymentPlan()

  const isReady =
    !isDeploymentPlanLoading &&
    !isPolicyScopesLoading &&
    !isExperiencesLoading &&
    !isPolicyDocumentLoading &&
    !isThemesLoading

  const onSubmit = useCallback(
    async (values: DeploymentPlanFormValues) => {
      try {
        const formData = mapValuesToPayload({
          values,
          isEntitledToContentAndDisclosure,
        })

        const { data } = await (duplicate || !isEditMode
          ? handleCreateDeploymentPlan({
              params: {
                formData,
              },
            })
          : handleUpdateDeploymentPlan({
              params: {
                deploymentPlanCode: code!,
                formData,
              },
            }))

        if (isEditMode) {
          queryClient.refetchQueries([ApiQueryKeys.deploymentPlan, { deploymentPlanCode: code }])
        }

        queryClient.refetchQueries(ApiQueryKeys.entitlements)

        showToast({ content: `Deployment plan ${isEditMode ? 'updated' : 'created'}`, type: 'success' })
        navigate(RoutesManager.deployment.deploymentPlansV2.view.root.getURL({ code: data.deployment.code }))
      } catch (e) {
        showToast({ content: `Failed to ${isEditMode ? 'update' : 'create'} deployment plan`, type: 'error' })
      }
    },
    [
      duplicate,
      isEditMode,
      code,
      queryClient,
      navigate,
      handleCreateDeploymentPlan,
      handleUpdateDeploymentPlan,
      isEntitledToContentAndDisclosure,
    ],
  )

  const value = useMemo(
    () => ({
      isReady,
      onSubmit,
      duplicate,
      deploymentPlan,
      policyScopes,
      experiences,
      policyDocuments,
      themes,
    }),
    [isReady, onSubmit, duplicate, deploymentPlan, policyScopes, experiences, policyDocuments, themes],
  )

  return <Provider value={value}>{children}</Provider>
}

export { useDeploymentPlanUpsertContext }
